import type { AppContext } from "../../app/app";
import { surface } from "../../llm/partner";
import { el } from "../dom";
import { requireProcessingConsent } from "../privacy";
import { renderReport } from "../report";

// SURFACE: patterns and blind spots across the whole tag set, on demand.
export async function renderSurface(ctx: AppContext): Promise<HTMLElement> {
  const root = el("section", {});
  root.append(
    el("span", { class: "label", text: "Surface" }),
    el("h1", { text: "Patterns and blind spots" }),
  );

  const tags = await ctx.repo.listTags({ viewer_id: ctx.viewer });
  if (new Set(tags.map((t) => t.person_id)).size === 0) {
    root.append(
      el(
        "div",
        { class: "notice" },
        el("p", {
          text:
            "Nothing to analyse yet. Rate a few people on a quality or two, " +
            "then come back for a read on your own tagging.",
        }),
        el("button", {
          class: "btn btn--primary",
          text: "Go and rate",
          on: { click: () => ctx.navigate({ name: "people" }) },
        }),
      ),
    );
    return root;
  }

  const output = el("div", {});

  const run = el("button", {
    class: "btn btn--primary",
    text: "Analyse my tags",
    on: {
      click: async () => {
        if (!(await requireProcessingConsent(ctx))) return;
        run.disabled = true;
        output.replaceChildren(el("p", { class: "lede", text: "Reading your tags..." }));
        try {
          const report = await surface(ctx.repo, ctx.provider, ctx.viewer);
          output.replaceChildren(renderReport(report));
        } catch (e) {
          output.replaceChildren(modelError(ctx, e));
        } finally {
          run.disabled = false;
        }
      },
    },
  });

  root.append(
    el("p", {
      class: "lede",
      text:
        "A read on your own tagging: where it concentrates, which qualities it " +
        "skews to, and where coverage is thin. Drawn only from your data.",
    }),
    el("div", { class: "panel-actions" }, run),
    output,
  );
  return root;
}

export function modelError(ctx: AppContext, e: unknown): HTMLElement {
  const message = e instanceof Error ? e.message : "Something went wrong.";
  return el(
    "div",
    { class: "notice" },
    el("p", { text: message }),
    el("button", {
      class: "btn btn--ghost",
      text: "Open settings",
      on: { click: () => ctx.navigate({ name: "settings" }) },
    }),
  );
}
